import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { adjustStock } from '@/hooks/useInventory';
import { friendlyError } from '@/lib/errors';
import Modal from '@/components/base/Modal';
import type { Product } from '@/lib/types';

type AdjustType = 'ajuste' | 'entrada' | 'salida';

interface InventoryAdjustModalProps {
  open: boolean;
  onClose: () => void;
  type: AdjustType;
  onDone: () => void;
  branchId?: string | null;
}

const SALIDA_REASONS = ['Producto dañado', 'Pérdida', 'Consumo interno', 'Otro'];
const AJUSTE_REASONS = [
  'Conteo físico',
  'Producto dañado',
  'Pérdida',
  'Error de inventario',
  'Corrección',
  'Otro',
];
const ENTRADA_REASONS = [
  'Compra a proveedor',
  'Devolución de cliente',
  'Transferencia entre sucursales',
  'Otro',
];

export default function InventoryAdjustModal({
  open,
  onClose,
  type,
  onDone,
  branchId,
}: InventoryAdjustModalProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      const { data } = await supabase.from('products').select('id, name').order('name');
      const stockMap = new Map<string, number>();
      if (branchId) {
        const { data: inv } = await supabase
          .from('branch_inventory')
          .select('product_id, stock')
          .eq('branch_id', branchId);
        for (const r of (inv ?? []) as { product_id: string; stock: number }[]) {
          stockMap.set(r.product_id, Number(r.stock ?? 0));
        }
      }
      const rows = ((data as { id: string; name: string }[]) ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        stock: stockMap.get(p.id) ?? 0,
      }));
      setProducts(rows as Product[]);
    };
    load();
    setSelected(null);
    setSearch('');
    setQuantity('');
    setNewQuantity('');
    setReason('');
    setReasonSelect(
      type === 'ajuste' ? AJUSTE_REASONS[0] : type === 'salida' ? SALIDA_REASONS[0] : ENTRADA_REASONS[0]
    );
    setNote('');
    setError(null);
  }, [open, type]);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const title =
    type === 'ajuste' ? 'Ajustar inventario' : type === 'entrada' ? 'Entrada de inventario' : 'Salida de inventario';

  const handleSave = async () => {
    setError(null);
    if (!selected) {
      setError('Selecciona un producto.');
      return;
    }

    let delta = 0;
    if (type === 'ajuste') {
      const qty = parseInt(newQuantity, 10);
      if (Number.isNaN(qty) || qty <= 0) {
        setError('Ingresa una cantidad válida.');
        return;
      }
      delta = qty;
    } else {
      const qty = parseInt(quantity, 10);
      if (Number.isNaN(qty) || qty <= 0) {
        setError('Ingresa una cantidad válida.');
        return;
      }
      delta = type === 'entrada' ? qty : -qty;
    }

    setSaving(true);
    try {
      await adjustStock({
        product_id: selected.id,
        quantity: delta,
        type,
        reason: reasonSelect || reason.trim() || null,
        note: note.trim() || null,
        branch_id: branchId ?? null,
      });
      onDone();
      onClose();
    } catch (err) {
      setError(friendlyError(err, 'No se pudo ajustar el inventario.'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Selector de producto */}
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">Producto</label>
          {selected ? (
            <div className="flex items-center justify-between rounded-md border border-primary-300 bg-primary-50 px-3 py-2.5">
              <span className="text-sm font-medium text-foreground-900 truncate">{selected.name}</span>
              <button
                onClick={() => setSelected(null)}
                className="text-xs font-medium text-primary-700 hover:underline cursor-pointer"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto..."
                autoFocus
                className={inputClass}
              />
              <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-background-200 divide-y divide-background-200">
                {filtered.slice(0, 50).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelected(p)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-background-100 cursor-pointer"
                  >
                    <span className="text-foreground-900 truncate">{p.name}</span>
                    <span className="text-xs text-foreground-500 ml-3 shrink-0">Stock: {p.stock}</span>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="px-3 py-3 text-sm text-foreground-500">Sin resultados.</p>
                )}
              </div>
            </>
          )}
        </div>

        {selected && (
          <>
            <div className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm">
              <span className="text-foreground-600">Stock actual</span>
              <span className="font-semibold text-foreground-950">{selected.stock}</span>
            </div>

            {type === 'ajuste' ? (
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Cantidad a agregar
                </label>
                <input
                  type="number"
                  min="1"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  placeholder="Ej. 10"
                  className={inputClass}
                />
                {newQuantity && !Number.isNaN(parseInt(newQuantity, 10)) && (
                  <p className="mt-1.5 text-xs text-foreground-500">
                    Nuevo stock: {selected.stock + parseInt(newQuantity, 10)}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Cantidad</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Ej. 5"
                  className={inputClass}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Motivo</label>
              <select
                value={reasonSelect}
                onChange={(e) => setReasonSelect(e.target.value)}
                className={inputClass}
              >
                {(type === 'ajuste' ? AJUSTE_REASONS : type === 'salida' ? SALIDA_REASONS : ENTRADA_REASONS).map(
                  (r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                Observación (opcional)
              </label>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Nota adicional"
                className={inputClass}
              />
            </div>
          </>
        )}

        {error && (
          <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}